import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { getUserProperty, setUserProperty } from '../common/user-properties/index.tsx';

const CROSS_JOIN_THIRD_PARTY_NUDGES_JIRA_VISITED_KEY =
	'cross.join.third.party.nudges.v2.jira.visited';
const SNOOZE_DAYS_FOR_7_DAYS = 7;
const ONE_DAY_IN_MILLISECONDS = 86400000;
const crossJoinThirdPartyNudgeVisitedCache = createLocalStorageProvider(
	'cross-join-third-party-nudges-v2-visited',
);
const VISITED_COUNT_KEY = 'CROSS_JOIN_THIRD_PARTY_NUDGES_V2_JIRA_VISITED_COUNT';

export const isFeatureVisited = (): boolean => {
	try {
		if (getFeatureVisited()) {
			return true;
		}
		snoozeFeatureFor7Days();
		if (!getFeatureVisited()) {
			fireErrorAnalytics({
				meta: {
					id: 'featureVisitedLocalStorageNotAvailabe',
					packageName: 'jiraCrossJoinThirdPartyNudges',
				},
			});
			return true;
		}
		return false;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'useFeatureVisited',
				packageName: 'jiraCrossThirdPartyJoinNudges',
			},
			error,
			skipSentry: true,
			sendToPrivacyUnsafeSplunk: true,
		});
		return true;
	}
};

const getFeatureVisited = (key = CROSS_JOIN_THIRD_PARTY_NUDGES_JIRA_VISITED_KEY) => {
	const snoozeUntilTimestampString = crossJoinThirdPartyNudgeVisitedCache.get(key);

	if (snoozeUntilTimestampString !== null) {
		const SNOOZE_UNTIL_TIMESTAMP = parseInt(snoozeUntilTimestampString, 10);
		const TIMESTAMP = new Date().getTime();
		if (!Number.isNaN(SNOOZE_UNTIL_TIMESTAMP) && TIMESTAMP < SNOOZE_UNTIL_TIMESTAMP) {
			return true;
		}
	}
	return false;
};

const setFeatureVisited = (
	numOfDaysSnoozed: number,
	key = CROSS_JOIN_THIRD_PARTY_NUDGES_JIRA_VISITED_KEY,
) => {
	const SNOOZE_UNTIL_TIMESTAMP = new Date().getTime() + ONE_DAY_IN_MILLISECONDS * numOfDaysSnoozed;
	crossJoinThirdPartyNudgeVisitedCache.set(key, SNOOZE_UNTIL_TIMESTAMP);
};

export const snoozeFeatureFor7Days = (key = CROSS_JOIN_THIRD_PARTY_NUDGES_JIRA_VISITED_KEY) => {
	setFeatureVisited(SNOOZE_DAYS_FOR_7_DAYS, key);
};

export const getFeatureVisitedCount = async (
	accountId: string,
	propertyKey: string = VISITED_COUNT_KEY,
): Promise<number> => {
	if (!accountId) return 0;

	const visitedCount = await getUserProperty<number>({
		accountId,
		propertyKey,
		defaultValue: 0,
	});

	return !Number.isNaN(visitedCount.value) ? visitedCount.value : 0;
};

export const increaseFeatureVisitedCount = async (
	accountId: string,
	incrementedCount: number,
	propertyKey = VISITED_COUNT_KEY,
) => {
	await setUserProperty<number>({
		accountId,
		propertyKey,
		propertyValue: incrementedCount,
	});
};
